'use client';

/**
 * useSearchAizSettings Hook
 *
 * Manages SearchAiz settings with multi-layer inheritance.
 * Provides:
 * - Settings resolution (Portal → Developer → User)
 * - User preference persistence
 * - Permission-aware updates
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import {
  resolveSettings,
  loadUserSettings,
  saveUserSettings,
  updateUserSetting,
  canUserModify,
  validateSetting,
  DEFAULT_SETTINGS,
} from '../utils/settingsResolver';
import type {
  SearchAizSettings,
  PartialSettings,
  LayeredSettings,
  SearchContext,
  SettingPath,
  SettingValue,
} from '../types';

// =============================================================================
// CONTEXT
// =============================================================================

interface SearchAizSettingsContextValue {
  /** Resolved settings */
  settings: SearchAizSettings;
  /** All layer settings (for debugging) */
  layeredSettings: LayeredSettings;
  /** Update a user setting */
  updateSetting: <P extends SettingPath>(path: P, value: SettingValue<P>) => void;
  /** Reset user settings to defaults */
  resetSettings: () => void;
  /** Check if user can modify a setting */
  canModify: (path: string) => boolean;
  /** Whether settings are loading */
  isLoading: boolean;
}

const SearchAizSettingsContext = createContext<SearchAizSettingsContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

export interface SearchAizSettingsProviderProps {
  /** Portal-level settings (highest priority ceiling) */
  portalSettings?: PartialSettings;
  /** Developer-level settings */
  developerSettings?: PartialSettings;
  /** Search context for applying context overrides */
  context?: SearchContext;
  /** Children to render */
  children: ReactNode;
}

export function SearchAizSettingsProvider({
  portalSettings = {},
  developerSettings = {},
  context,
  children,
}: SearchAizSettingsProviderProps) {
  const [userSettings, setUserSettings] = useState<PartialSettings>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load user settings on mount
  useEffect(() => {
    const stored = loadUserSettings();
    setUserSettings(stored);
    setIsLoading(false);
  }, []);

  // Resolve settings whenever any layer changes
  const layeredSettings = useMemo(
    () => resolveSettings(portalSettings, developerSettings, userSettings, context),
    [portalSettings, developerSettings, userSettings, context]
  );

  /**
   * Update a single user setting
   */
  const updateSetting = useCallback(
    <P extends SettingPath>(path: P, value: SettingValue<P>) => {
      // Validate the setting
      const validation = validateSetting(
        path,
        value,
        layeredSettings.resolved
      );

      if (!validation.valid) {
        console.warn(`Invalid setting value for ${path}:`, validation.error);
        return;
      }

      // Check if user can modify this setting
      if (!canUserModify(path, layeredSettings.resolved)) {
        console.warn(`User cannot modify setting: ${path}`);
        return;
      }

      // Update state and persist
      setUserSettings((prev) => {
        const updated = updateUserSetting(path, validation.value, prev);
        saveUserSettings(updated);
        return updated;
      });
    },
    [layeredSettings.resolved]
  );

  /**
   * Reset all user settings
   */
  const resetSettings = useCallback(() => {
    setUserSettings({});
    saveUserSettings({});
  }, []);

  /**
   * Check if user can modify a setting
   */
  const canModify = useCallback(
    (path: string) => canUserModify(path, layeredSettings.resolved),
    [layeredSettings.resolved]
  );

  const value: SearchAizSettingsContextValue = {
    settings: layeredSettings.resolved,
    layeredSettings,
    updateSetting,
    resetSettings,
    canModify,
    isLoading,
  };

  return (
    <SearchAizSettingsContext.Provider value={value}>
      {children}
    </SearchAizSettingsContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access SearchAiz settings
 */
export function useSearchAizSettings(): SearchAizSettingsContextValue {
  const context = useContext(SearchAizSettingsContext);

  if (!context) {
    // Return default settings if not in provider
    // This allows components to work standalone
    return {
      settings: DEFAULT_SETTINGS,
      layeredSettings: {
        portal: {},
        developer: {},
        user: {},
        resolved: DEFAULT_SETTINGS,
      },
      updateSetting: () => {
        console.warn('useSearchAizSettings: Not in provider, updates are no-ops');
      },
      resetSettings: () => {},
      canModify: () => false,
      isLoading: false,
    };
  }

  return context;
}

// =============================================================================
// SELECTOR HOOKS
// =============================================================================

/**
 * Hook to get a specific setting value
 */
export function useSearchAizSetting<P extends SettingPath>(
  path: P
): [SettingValue<P> | undefined, (value: SettingValue<P>) => void, boolean] {
  const { settings, updateSetting, canModify } = useSearchAizSettings();

  // Get nested value from settings
  const value = useMemo(() => {
    const parts = path.split('.');
    let current: unknown = settings;

    for (const part of parts) {
      if (current && typeof current === 'object') {
        current = (current as Record<string, unknown>)[part];
      } else {
        return undefined;
      }
    }

    return current as SettingValue<P>;
  }, [settings, path]);

  const setValue = useCallback(
    (newValue: SettingValue<P>) => {
      updateSetting(path, newValue);
    },
    [updateSetting, path]
  );

  const canEdit = canModify(path);

  return [value, setValue, canEdit];
}

/**
 * Hook to get AI feature settings
 */
export function useAiSettings() {
  const { settings } = useSearchAizSettings();
  return settings.ai;
}

/**
 * Hook to get available and default sources
 */
export function useSourceSettings() {
  const { settings, canModify } = useSearchAizSettings();
  return {
    available: settings.sources.available,
    default: settings.sources.default,
    canChangeDefault: canModify('sources.default'),
    canSelectPerSearch: settings.sources.userCanSelectPerSearch,
  };
}

/**
 * Hook to get UI settings
 */
export function useUiSettings() {
  const { settings } = useSearchAizSettings();
  return settings.ui;
}

/**
 * Hook to get result display settings
 */
export function useResultSettings() {
  const { settings } = useSearchAizSettings();
  return settings.results;
}

export default useSearchAizSettings;
